import { useCallback, useState } from 'react';
import { extractIdentityKey, makeSafeArrayValue } from '../utils/multiSelectUtils';

interface UseMultiSelectFieldParams {
    initialValue: unknown;
    onUpdate: (values: unknown[]) => void;
    /** When true, values are treated as reference objects and compared by `id` */
    isReferenceType?: boolean;
}

interface UseMultiSelectFieldReturn {
    selectedValues: unknown[];
    setSelectedValues: (values: unknown[]) => void;
    addValue: (value: unknown) => void;
    removeValue: (valueToRemove: unknown) => void;
    updateFromExternal: (value: unknown) => void;
}

export function useMultiSelectField({
    initialValue,
    onUpdate,
    isReferenceType = false
}: UseMultiSelectFieldParams): UseMultiSelectFieldReturn {
    const [selectedValues, setSelectedValues] = useState(() => makeSafeArrayValue(initialValue));

    const addValue = useCallback(
        (value: unknown) => {
            const valueToAdd = isReferenceType
                ? value
                : typeof value === 'object' && value !== null
                  ? (value as any).label || value
                  : value;

            const newValues = [...selectedValues, valueToAdd];

            const uniqueValues = isReferenceType
                ? newValues.filter(
                      (item, index, self) =>
                          self.findIndex((other) => extractIdentityKey(other) === extractIdentityKey(item)) === index
                  )
                : [...new Set(newValues)];

            setSelectedValues(uniqueValues);
            onUpdate(uniqueValues);
        },
        [selectedValues, onUpdate, isReferenceType]
    );

    const removeValue = useCallback(
        (valueToRemove: unknown) => {
            const keyToRemove = isReferenceType ? extractIdentityKey(valueToRemove) : valueToRemove;

            const newValues = selectedValues.filter((v) => {
                const key = isReferenceType ? extractIdentityKey(v) : v;
                return key !== keyToRemove;
            });

            setSelectedValues(newValues);
            onUpdate(newValues);
        },
        [selectedValues, onUpdate, isReferenceType]
    );

    const updateFromExternal = useCallback((value: unknown) => {
        setSelectedValues(makeSafeArrayValue(value));
    }, []);

    return {
        selectedValues,
        setSelectedValues,
        addValue,
        removeValue,
        updateFromExternal
    };
}
